/**
 * Text measurement + helper utilities for the Tapestry view.
 *
 * DOM-only — mounts hidden SVG fragments offscreen to measure text widths,
 * converts `<line>` elements to `<path>`, parses tapgen-style id hints, and
 * filters out guide paths that are too curved or tilted to host readable
 * text. All measurement assumes the Hand1-Regular webfont is loaded; call
 * `ensureFontReady()` before measuring.
 */

const SVG_NS = 'http://www.w3.org/2000/svg';
export const DEFAULT_FONT = 'Hand1-Regular';
const SAFARI_SCALAR = 0.65;

export const MIN_FONT_SIZE = 18;

const isSafari =
  typeof navigator !== 'undefined' &&
  /^((?!chrome|android).)*safari/i.test(navigator.userAgent) &&
  navigator.userAgent.indexOf('CriOS') === -1 &&
  navigator.userAgent.indexOf('FxiOS') === -1;

/**
 * Resolves once the Hand1-Regular font is loaded and ready for measurement.
 * Returns immediately on SSR or in environments without `document.fonts`.
 */
const fontReadyPromises = new Map();
export function ensureFontReady(fontFamily = DEFAULT_FONT) {
  if (typeof document === 'undefined' || !document.fonts) return Promise.resolve();
  // Cache per family — a single shared promise would load only the first font
  // requested and report every other one as "ready" without loading it.
  if (!fontReadyPromises.has(fontFamily)) {
    fontReadyPromises.set(
      fontFamily,
      document.fonts.load(`24px "${fontFamily}"`).catch(() => {})
    );
  }
  return fontReadyPromises.get(fontFamily);
}

/**
 * Measure a string's rendered bbox width/height at a given font size by
 * mounting a throwaway SVG `<text>` offscreen and reading `getBBox`. Widths
 * are in CSS pixels.
 */
export function measureText(text, fontSize, fontFamily = DEFAULT_FONT) {
  if (typeof document === 'undefined') return { width: 0, height: 0 };
  const svg = document.createElementNS(SVG_NS, 'svg');
  const textEl = document.createElementNS(SVG_NS, 'text');
  textEl.setAttribute('font-family', `'${fontFamily}', Arial, Helvetica, sans-serif`);
  textEl.setAttribute('font-size', fontSize + 'px');
  textEl.textContent = text;
  svg.appendChild(textEl);
  svg.style.cssText = 'position:absolute;left:-9999px;top:-9999px;visibility:hidden';
  document.body.appendChild(svg);
  const bbox = textEl.getBBox();
  document.body.removeChild(svg);
  return {
    width: bbox.width * (isSafari ? SAFARI_SCALAR : 1),
    height: bbox.height,
  };
}

/**
 * Iteratively fit a phrase to a target length by scaling the font size up
 * or down until the measured width crosses the target, then backs off a hair
 * for breathing room. Inner-symbol text isn't clipped to its guide (it's an
 * HTML span), so we fill most of the guide rather than leaving a big margin —
 * keeps single words from reading tiny. Returns a size in CSS pixels, or null
 * if the result would be below `minSize`.
 */
function fitFontSize(text, startSize, targetLength, fontFamily = DEFAULT_FONT, minSize = MIN_FONT_SIZE) {
  if (!text || !targetLength) return null;
  let size = startSize;
  let width = measureText(text, size, fontFamily).width;
  if (width === 0) return null;

  const scalar = width < targetLength ? 1.1 : 0.9;
  let final = size;
  let safety = 40;
  while (safety-- > 0 && (scalar > 1 ? width < targetLength : width > targetLength)) {
    final = size;
    size *= scalar;
    width = measureText(text, size, fontFamily).width;
  }
  if (scalar < 1) final = size;
  final *= 0.97;
  return final >= minSize ? final : null;
}

/** Shorter phrases get larger initial fonts — mirrors tapgen's behavior. */
function initialFontSize(wordCount, maxSize) {
  const t = Math.max(0, Math.min(1, wordCount / 8));
  return maxSize * 0.65 - (maxSize * 0.65 - MIN_FONT_SIZE) * t;
}

/**
 * Walk `candidates` in order and return the first one that fits on a path
 * of length `pathLength`. Skips any phrase already in `usedPhrases` so the
 * same text never appears twice in one composition.
 */
export function pickFittingPhrase(candidates, pathLength, usedPhrases, opts = {}) {
  const { minSize = MIN_FONT_SIZE, maxSize = 96, fontFamily = DEFAULT_FONT } = opts;
  for (const text of candidates) {
    if (!text || usedPhrases.has(text)) continue;
    const wordCount = text.trim().split(/\s+/).length;
    if (wordCount === 0) continue;
    const start = Math.min(maxSize, initialFontSize(wordCount, maxSize));
    // fitFontSize only respects the lower bound, so a short word on a long path
    // grows past maxSize — clamp it back so inner-symbol words stay capped.
    const fitted = fitFontSize(text, start, pathLength, fontFamily, minSize);
    if (fitted && fitted >= minSize) {
      return { text, fontSize: Math.min(fitted, maxSize) };
    }
  }
  return null;
}

/**
 * Convert a `<line>` element to a `<path>` so it can host a `<textPath>`.
 * Preserves the element's id, which textPath `href` references.
 */
export function lineToPath(lineEl) {
  const doc = lineEl.ownerDocument;
  const path = doc.createElementNS(SVG_NS, 'path');
  if (lineEl.id) path.id = lineEl.id;
  const reversed = +lineEl.getAttribute('x2') < +lineEl.getAttribute('x1');
  const x1 = lineEl.getAttribute(reversed ? 'x2' : 'x1');
  const x2 = lineEl.getAttribute(reversed ? 'x1' : 'x2');
  const y1 = lineEl.getAttribute(reversed ? 'y2' : 'y1');
  const y2 = lineEl.getAttribute(reversed ? 'y1' : 'y2');
  path.setAttribute('d', `M ${x1} ${y1} L ${x2} ${y2}`);
  lineEl.parentNode.replaceChild(path, lineEl);
  return path;
}

/**
 * Is this guide path usable for straight-reading text? Checks curviness
 * (arc length / chord length) and chord angle from horizontal. Spiral- or
 * zigzag-shaped decorative guides fail this and get skipped.
 */
export function isPathUsable(pathEl, { maxCurviness = 1.15, maxAngleDeg = 25 } = {}) {
  if (!pathEl || typeof pathEl.getTotalLength !== 'function') return false;
  let length;
  try {
    length = pathEl.getTotalLength();
  } catch {
    return false;
  }
  if (!length || length < 4) return false;

  let start, end;
  try {
    start = pathEl.getPointAtLength(0);
    end = pathEl.getPointAtLength(length);
  } catch {
    return false;
  }

  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const chord = Math.hypot(dx, dy);
  if (chord < 1e-3) return false;

  if (length / chord > maxCurviness) return false;

  const angleDeg = Math.abs((Math.atan2(dy, dx) * 180) / Math.PI);
  // Allow near-180° paths too — they read right-to-left but still horizontally.
  if (angleDeg > maxAngleDeg && Math.abs(angleDeg - 180) > maxAngleDeg) {
    return false;
  }
  return true;
}

/**
 * Parse the `lines:N` / `color:#HEX` / `toptext` hints that tapgen encodes
 * in a textpath element's id.
 */
export function parseTextpathHints(id) {
  if (!id) return { lines: 1, color: null, isToptext: false };
  const linesMatch = id.match(/lines:(\d+)/);
  const colorMatch = id.match(/color:(#[a-fA-F0-9]{6})/);
  return {
    lines: linesMatch ? +linesMatch[1] : 1,
    color: colorMatch ? colorMatch[1] : null,
    isToptext: /toptext/i.test(id),
  };
}
